import { useForm, useWatch } from "react-hook-form";
import { DMModal, DMModalContent, DMModalTitle } from "../../../shared/DMModal";
import Button from "../../../student/components/generic/button";
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { executeQuery } from "../../../utils";
import { useParams } from "react-router-dom";
import { IndividualQuoteRequestAmount } from "./IndividualQuoteRequestAmount";
import clsx from "clsx";

interface FormInputs {
  individualPlus: number | undefined;
  individualIntegral: number | undefined;
}

type LicenseType = "plus" | "integral" | "both";

export const IndividualQuoteRequestModal: React.FC<{
  individualPlus: number | undefined;
  individualIntegral: number | undefined;
  pricePerPlus: number;
  pricePerIntegral: number;
  nces: string;
  visible: boolean;
  onClose: () => void;
}> = ({
  individualPlus,
  individualIntegral,
  pricePerPlus,
  pricePerIntegral,
  nces,
  visible,
  onClose,
}) => {
  const { renewalId } = useParams();
  const [isDownloading, setIsDownloading] = useState(false);
  const [message, setMessage] = useState<{
    type: "error" | "info";
    text: string;
  } | null>(null);

  const previousLicenseType: LicenseType =
    individualPlus && individualIntegral
      ? "both"
      : individualPlus
      ? "plus"
      : "integral";
  const useWideLayout = previousLicenseType === "plus";

  const [licenseType, setLicenseType] =
    useState<LicenseType>(previousLicenseType);

  const {
    control,
    register,
    handleSubmit,
    formState: { errors: formErrors },
  } = useForm<FormInputs>({
    defaultValues: {
      individualPlus: individualPlus ?? 0,
      individualIntegral: individualIntegral ?? 0,
    },
  });

  const watcher = useWatch({ control });

  const showIntegralField =
    licenseType === "both" || licenseType === "integral";
  const showPlusField = licenseType === "both" || licenseType === "plus";

  const { mutate: getQuoteMutation } = useMutation({
    mutationFn: async ({
      licenseTier,
      licenseCount,
    }: {
      licenseTier: "Plus" | "Integral";
      licenseCount: number;
    }) => {
      const res = await executeQuery({
        path: "/quote_request",
        method: "post",
        additionalHeaders: { Accept: "application/pdf" },
        responseType: "blob",
        returnHeaders: true,
        requestBody: {
          renewalToken: encodeURIComponent(renewalId ?? ""),
          type: "Individual License",
          licenseTier,
          individualSiteType: "School",
          licenseCount,
          id: nces,
        },
      });

      const { responseData, responseHeaders } = res;

      if (
        responseHeaders["content-type"] === "application/pdf" &&
        responseData
      ) {
        return { responseData, responseHeaders };
      } else {
        try {
          return {
            responseData: await new Response(responseData).json(),
            responseHeaders,
          };
        } catch (e) {
          return { success: false, message: `Error: ${e}` };
        }
      }
    },
  });

  const downloadQuote = ({
    licenseTier,
    licenseCount,
  }: {
    licenseTier: "Plus" | "Integral";
    licenseCount: number;
  }) => {
    return new Promise<boolean>((resolve) => {
      getQuoteMutation(
        {
          licenseTier,
          licenseCount,
        },
        {
          onSuccess({ responseHeaders, responseData }) {
            if (responseHeaders["content-type"] === "application/pdf") {
              const blob = new Blob([responseData], {
                type: "application/pdf",
              });
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `DeltaMath Quote ${licenseTier} ${licenseCount} ${new Date().toLocaleDateString()}.pdf`;
              a.click();
              window.URL.revokeObjectURL(url);
              resolve(true);
            } else {
              setMessage({ type: "info", text: responseData.message });
              resolve(false);
            }
          },
          onError() {
            setMessage({ type: "error", text: "Unable to download quote" });
            resolve(false);
          },
        }
      );
    });
  };

  const handlePlusLicenseSelect = async (selectedLicenseType: LicenseType) => {
    if (selectedLicenseType === "both") {
      throw new Error("Unable to select both license types");
    }
    if (!watcher.individualPlus) {
      setMessage({
        type: "error",
        text: "Please enter a number of licenses before downloading.",
      });
      return;
    }
    setIsDownloading(true);
    const success = await downloadQuote({
      licenseTier: selectedLicenseType === "plus" ? "Plus" : "Integral",
      licenseCount: watcher.individualPlus,
    });
    setIsDownloading(false);
    if (success) {
      onClose();
    }
  };

  const onSubmit = async (data: FormInputs) => {
    if (useWideLayout) {
      return;
    }
    setIsDownloading(true);
    const { individualPlus, individualIntegral } = data;
    let success = true;

    if ((licenseType === "both" || licenseType === "plus") && individualPlus) {
      success = await downloadQuote({
        licenseTier: "Plus",
        licenseCount: individualPlus,
      });
    }

    if (
      success &&
      (licenseType === "both" || licenseType === "integral") &&
      individualIntegral
    ) {
      success = await downloadQuote({
        licenseTier: "Integral",
        licenseCount: individualIntegral,
      });
    }

    setIsDownloading(false);

    if (success) {
      onClose();
    }
  };

  return (
    <DMModal
      className={clsx(
        useWideLayout ? "max-w-[800px] !py-10 sm:!px-12" : "!py-10"
      )}
      visible={visible}
      onClose={onClose}
    >
      <DMModalTitle>Get an Individual License Quote</DMModalTitle>
      <DMModalContent>
        <div className="flex w-full justify-center">
          <form
            className={clsx(
              "flex w-full flex-col gap-8",
              !useWideLayout && "max-w-sm"
            )}
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="flex flex-col gap-6">
              {!useWideLayout && (
                <div className="flex flex-col gap-1">
                  <label
                    className="text-sm font-bold text-dm-gray-800"
                    htmlFor="licenseType"
                  >
                    License Type(s)
                  </label>
                  <select
                    className="h-10 rounded border-dm-gray-200 text-sm focus:border-dm-brand-blue-500"
                    id="licenseType"
                    value={licenseType}
                    disabled={isDownloading}
                    onChange={(e) =>
                      setLicenseType(e.target.value as LicenseType)
                    }
                  >
                    <option value="plus">PLUS</option>
                    <option value="integral">INTEGRAL</option>
                    {previousLicenseType === "both" && (
                      <option value="both">PLUS and INTEGRAL</option>
                    )}
                  </select>
                </div>
              )}

              {showPlusField && (
                <div className="flex flex-col gap-1">
                  <label
                    className="text-sm font-bold text-dm-gray-800"
                    htmlFor="individualPlus"
                  >
                    {useWideLayout
                      ? "Number of Licenses"
                      : "Number of PLUS Licenses"}
                  </label>
                  <input
                    className="h-10 w-36 rounded border-dm-gray-200 text-sm focus:border-dm-brand-blue-500"
                    type="number"
                    id="individualPlus"
                    disabled={isDownloading}
                    min="0"
                    {...register("individualPlus", {
                      valueAsNumber: true,
                      validate: {
                        required: (value) =>
                          showPlusField && !value
                            ? "This field is required"
                            : true,
                      },
                    })}
                  />
                  {formErrors.individualPlus ? (
                    <p className="text-xs text-dm-error-500">
                      {formErrors.individualPlus.message}
                    </p>
                  ) : individualPlus ? (
                    <p className="text-xs text-dm-charcoal-500">
                      <i className="fal fa-info-circle" /> You purchased{" "}
                      {individualPlus} PLUS licenses last year.
                    </p>
                  ) : null}
                </div>
              )}

              {showIntegralField && (
                <div className="flex flex-col gap-1">
                  <label
                    className="text-sm font-bold text-dm-gray-800"
                    htmlFor="individualIntegral"
                  >
                    Number of INTEGRAL Licenses
                  </label>
                  <input
                    className="h-10 w-36 rounded border-dm-gray-200 text-sm focus:border-dm-brand-blue-500"
                    type="number"
                    id="individualIntegral"
                    disabled={isDownloading}
                    min="0"
                    {...register("individualIntegral", {
                      valueAsNumber: true,
                      validate: {
                        required: (value) =>
                          showIntegralField && !value
                            ? "This field is required"
                            : true,
                      },
                    })}
                  />
                  {formErrors.individualIntegral ? (
                    <p className="text-xs text-dm-error-500">
                      {formErrors.individualIntegral.message}
                    </p>
                  ) : individualIntegral ? (
                    <p className="text-xs text-dm-charcoal-500">
                      <i className="fal fa-info-circle" /> You purchased{" "}
                      {individualIntegral} PLUS licenses last year.
                    </p>
                  ) : null}
                </div>
              )}
            </div>

            <div className="flex flex-col gap-4">
              <IndividualQuoteRequestAmount
                licenseType={licenseType}
                previousLicenseType={previousLicenseType}
                individualPlus={watcher.individualPlus ?? 0}
                individualIntegral={watcher.individualIntegral ?? 0}
                pricePerPlus={pricePerPlus}
                pricePerIntegral={pricePerIntegral}
                onSelect={handlePlusLicenseSelect}
                isDownloading={isDownloading}
                disabled={message?.type === "info"}
              />
              <p
                className={clsx(
                  "text-sm font-bold italic",
                  useWideLayout && "w-full text-center"
                )}
              >
                Individual licenses do not include admin portal access.
              </p>
            </div>

            {message?.type === "error" && (
              <p className="w-full rounded bg-dm-error-100 p-2 text-center text-sm text-dm-error-500">
                {message.text}
              </p>
            )}

            <div className="flex flex-col gap-2">
              {!useWideLayout && (
                <Button
                  className="w-full"
                  isLoading={isDownloading}
                  disabled={message?.type === "info"}
                >
                  Download Quotes
                </Button>
              )}
              {message?.type === "info" && (
                <p
                  className={clsx(
                    "flex h-10 w-full items-center justify-center rounded-lg text-sm font-bold",
                    useWideLayout
                      ? "border border-dm-warning-500 bg-dm-warning-100"
                      : "border-dm-background-blue-100"
                  )}
                >
                  {message.text}
                </p>
              )}
            </div>
          </form>
        </div>
      </DMModalContent>
    </DMModal>
  );
};
